import type { AllMembershipPlanType } from '@frontend/api-client';
import { getMembershipPlanDisplayPricing, type MarketedMembershipPlanType } from '@frontend/api-client';
import type { PromotionDetails } from '@frontend/api-types';
import { colors } from '@frontend/constants';
import { formatCurrencyNatural } from '@frontend/duck-tape';
import type { SubscriptionPlan } from '@frontend/react-domain';
import { usePromotionTranslation } from '@frontend/react-domain';
import { Button, Divider, Icon, LinkButton, Text, TextInput, XStack, YStack } from '@frontend/web-react';
import { CalloutBadge } from '@frontend/web-react/components/Primitives/Badges';
import { SkeletonPlanCard } from '@frontend/web/components/DomainSpecific/Paywall/SkeletonPlanCard';
import { usePricingTerminology } from '@frontend/web/context';
import { getValidatedPromoInput, useLazyRetrievePromotionDetailsQuery, useMembershipPlans } from '@frontend/web/hooks';
import { handleMutation } from '@frontend/web/utils';
import { useEffect, useMemo, useRef, useState } from 'react';

type SubscriptionListProps = {
  canCancel?: boolean;
  currentMembershipPlanType?: AllMembershipPlanType;
  initialSelectedPlanType?: AllMembershipPlanType;
  onContinue: (planType: MarketedMembershipPlanType, promotion?: SubscriptionPlan['promotion']) => void;
  onPlanCancel?: () => void;
  onPromotionChange?: (promotion: PromotionDetails | undefined) => void;
};

export const SubscriptionList = ({
  canCancel = false,
  currentMembershipPlanType,
  initialSelectedPlanType,
  onContinue,
  onPlanCancel,
  onPromotionChange,
}: SubscriptionListProps) => {
  const { pricingTerminology, showCredits } = usePricingTerminology();
  const promoCodeInputRef = useRef<HTMLInputElement>(null);
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const retrievePromotionDetailsHookResult = useLazyRetrievePromotionDetailsQuery();
  const {
    actions: { handleApplyPromoCode, resetPromoCode },
    state: { isApplyPromoCodeLoading, promoCode, promoCodeErrorMessage, promotionDescription, promotionDetails },
  } = usePromotionTranslation({
    defaultPromoCode: '',
    firstPurchaseData: undefined,
    handleMutation,
    retrievePromotionDetailsHookResult,
  });

  const validatedPromo = getValidatedPromoInput({
    promoCode,
    promotionId: promotionDetails?.promotionId,
  });

  const { displayPlanLookup, displaySelectablePlans, isError, isLoading, pricingState, refetch } = useMembershipPlans({
    validatedPromo,
  });

  const filteredSelectablePlans = useMemo(
    () => displaySelectablePlans.filter((plan) => plan.planCode !== 'A_LA_CARTE'),
    [displaySelectablePlans],
  );
  const availableSubscriptionPlans = useMemo(
    () => filteredSelectablePlans.map((plan) => plan.planType),
    [filteredSelectablePlans],
  );
  const [selectedPlanType, setSelectedPlanType] = useState<MarketedMembershipPlanType | undefined>();

  const selectedPlanData = selectedPlanType ? displayPlanLookup[selectedPlanType] : undefined;
  const selectedPlanPricing = getMembershipPlanDisplayPricing(selectedPlanData);
  const price = selectedPlanPricing?.primaryPrice;

  const hasPromotion = Boolean(promotionDetails && selectedPlanType);
  const isPricingTransitioning = pricingState === 'transitioning';
  useEffect(() => {
    onPromotionChange?.(promotionDetails ?? undefined);
  }, [promotionDetails, onPromotionChange]);

  useEffect(() => {
    if (selectedPlanType && !availableSubscriptionPlans.includes(selectedPlanType)) {
      setSelectedPlanType(undefined);
      return;
    }

    if (
      !selectedPlanType &&
      initialSelectedPlanType &&
      initialSelectedPlanType !== currentMembershipPlanType &&
      availableSubscriptionPlans.includes(initialSelectedPlanType as MarketedMembershipPlanType)
    ) {
      setSelectedPlanType(initialSelectedPlanType as MarketedMembershipPlanType);
    }
  }, [availableSubscriptionPlans, currentMembershipPlanType, initialSelectedPlanType, selectedPlanType]);

  if (isLoading) {
    return (
      <YStack gap="md">
        <YStack bg="lineGray" height={24} radius="md" width="60%" />
        <YStack gap="md">
          <SkeletonPlanCard borderColor="borderTertiary" />
          <SkeletonPlanCard borderColor="borderTertiary" />
          <SkeletonPlanCard borderColor="borderTertiary" />
        </YStack>
      </YStack>
    );
  }

  if (isError) {
    return (
      <YStack alignItems="center" gap="md" justifyContent="center" p="md">
        <Icon className="text-red-500" name="IconAlertCircle" size={48} />
        <Text type="h4">Your plans took a duck dive.</Text>
        <Text color="secondary" type="p-sm">
          {"Don't worry, we'll bring them back. Try again."}
        </Text>
        <Button label="Try again" onClick={() => refetch()} size="md" variant="primary" />
      </YStack>
    );
  }

  const handleContinue = () => {
    if (!selectedPlanType) {
      return;
    }
    const promotion =
      promoCode && promotionDescription && promotionDetails
        ? { promoCode, promotionDescription, promotionDetails }
        : undefined;
    onContinue(selectedPlanType, promotion);
  };

  const handleCancel = () => {
    onPlanCancel?.();
  };

  return (
    <YStack gap="md">
      <YStack aria-label="Select subscription plan" gapY="md" role="radiogroup">
        {availableSubscriptionPlans.map((planType) => {
          const isCurrentPlan = currentMembershipPlanType ? planType === currentMembershipPlanType : false;
          const isSelected = selectedPlanType ? selectedPlanType === planType : isCurrentPlan;
          const planData = displayPlanLookup[planType];
          const planPricing = getMembershipPlanDisplayPricing(planData);
          if (!planData) return null;

          return (
            <XStack
              alignItems="center"
              aria-checked={isSelected}
              borderColor={isSelected ? 'borderPrimary' : 'borderTertiary'}
              borderWidth={2}
              gapX="lg"
              key={planType}
              onClick={() => setSelectedPlanType(planType)}
              p="md"
              radius="lg"
              role="radio"
              tabIndex={0}
            >
              <Icon name={isSelected ? 'IconCircleCheckFilled' : 'IconCircle'} size={24} />

              <YStack flex={1} gapY="xs">
                <XStack alignItems="center" gap="sm">
                  <Text type="h5">{planData.label}</Text>
                  {isCurrentPlan ? (
                    <CalloutBadge className="bg-neutral-100">CURRENT</CalloutBadge>
                  ) : planData.planCode === 'HOUSEHOLD' ? (
                    <CalloutBadge>MOST POPULAR</CalloutBadge>
                  ) : null}
                </XStack>

                <XStack alignItems="center" gapX="xs">
                  {isPricingTransitioning ? (
                    <YStack gapY="xs">
                      <YStack bg="lineGray" height={20} radius="md" width={80} />
                      <YStack bg="lineGray" height={16} radius="md" width={56} />
                    </YStack>
                  ) : (
                    <>
                      <Text style={{ lineHeight: 20 }} type="h3">
                        {formatCurrencyNatural(planPricing?.primaryPrice ?? planData.price)}
                      </Text>
                      <Text style={{ color: colors.neutral[600], lineHeight: 16 }}>/mo</Text>
                    </>
                  )}
                </XStack>
                {!isPricingTransitioning && planPricing?.secondaryPrice ? (
                  <Text className="line-through" color="tertiary" type="p-sm">
                    {formatCurrencyNatural(planPricing.secondaryPrice)}/mo
                  </Text>
                ) : null}
              </YStack>

              <YStack alignItems="end">
                {showCredits ? (
                  <Text className="text-neutral-900" type="p-sm">
                    {planData.creditsPerMonth} {pricingTerminology.unit_term_plural}
                  </Text>
                ) : null}
                <Text className="text-neutral-900" type="p-sm">
                  {planData.estimatedTasksPerMonth.min} - {planData.estimatedTasksPerMonth.max} tasks
                </Text>
              </YStack>
            </XStack>
          );
        })}
      </YStack>

      {selectedPlanType ? (
        promotionDescription ? (
          <YStack gapY="sm">
            <Divider />
            <YStack gapY="xs">
              <Text type="h6">Promotion</Text>
              <XStack
                alignItems="center"
                bg="trueWhite"
                borderColor="borderPrimary"
                borderWidth={2}
                gapX="md"
                p="lg"
                radius="lg"
              >
                <XStack alignItems="center" flex={1} gapX="md">
                  <Icon className="text-textSecondary" name="IconAwardFilled" size={24} />
                  <YStack flexShrink={1}>
                    <Text type="h5">{`Promo code: ${promoCode}`}</Text>
                    <XStack flexShrink={1}>
                      <Text type="p-sm">{promotionDescription}</Text>
                    </XStack>
                  </YStack>
                </XStack>
                <YStack>
                  <Icon
                    name="IconX"
                    onClick={() => {
                      resetPromoCode();
                      setPromoCodeInput('');
                      promoCodeInputRef.current?.blur();
                    }}
                  />
                </YStack>
              </XStack>
            </YStack>
          </YStack>
        ) : (
          <YStack gapY="md">
            <Divider />
            <YStack gapY="xs">
              <Text type="h6">Promo code</Text>
              <TextInput
                disabled={isApplyPromoCodeLoading}
                error={promoCodeErrorMessage}
                onChange={(val) => {
                  setPromoCodeInput(val);
                }}
                placeholder="Enter promo code here"
                ref={promoCodeInputRef}
                rightSection={
                  <XStack alignItems="center" gapX="sm" px="lg">
                    <LinkButton
                      color="primary"
                      disabled={!promoCodeInput.trim() || isApplyPromoCodeLoading}
                      isLoading={isApplyPromoCodeLoading}
                      label="Apply"
                      onClick={() => {
                        const trimmedCode = promoCodeInput.trim();
                        handleApplyPromoCode(trimmedCode);
                        setPromoCodeInput(trimmedCode);
                        promoCodeInputRef.current?.blur();
                      }}
                    />
                  </XStack>
                }
                rightSectionWidth="80px"
                value={promoCodeInput}
              />
            </YStack>
          </YStack>
        )
      ) : null}

      <YStack gapY="md">
        {isPricingTransitioning && selectedPlanType ? (
          <XStack alignItems="center" gapX="sm">
            <Icon name="IconCoin" size={24} />
            <YStack gapY="xs">
              <YStack bg="lineGray" height={18} radius="md" width={144} />
              <YStack bg="lineGray" height={24} radius="md" width={96} />
            </YStack>
          </XStack>
        ) : null}
        {!isPricingTransitioning && hasPromotion && price !== undefined ? (
          <XStack alignItems="center" gapX="sm">
            <Icon name="IconCoin" size={24} />
            <XStack alignItems="center" gapX="md">
              <Text type="h5">{`What you'll pay today`}</Text>
              <Text type="h3">{formatCurrencyNatural(price)}</Text>
            </XStack>
          </XStack>
        ) : null}
        <Button
          disabled={!selectedPlanData || selectedPlanType === currentMembershipPlanType || isPricingTransitioning}
          label="Continue"
          onClick={handleContinue}
          size="md"
          variant="primary"
        />
        {canCancel ? (
          <XStack justifyContent="center" pt="sm">
            <LinkButton
              className="shrink-0 grow-0"
              color="primary"
              label="Cancel Membership"
              onClick={handleCancel}
              textClassName="font-normal text-textPrimary"
              type="h5"
              withUnderline
            />
          </XStack>
        ) : null}
      </YStack>
    </YStack>
  );
};
